import {
  Autocomplete,
  Box,
  FormControlLabel,
  InputAdornment,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { ColorPickerInput } from 'src/components/SCADA/colorPickerInput/ColorPickerInput';
import { strokeTicknessOptions } from 'src/components/SCADA/config/elementsOptions';
import { useCustomDashboardContext } from 'src/contexts/customDashboardContext/customDashboard.context';

export const RectangleDrawer = () => {
  const theme = useTheme();
  const greyColor = theme.palette.grey[500];

  const [formValues, setFormValues] = useState({
    width: '',
    height: '',
    fillColor: '',
    strokeColor: '',
    strokeThickness: '',
    isLockRatio: false,
    lockedWidth: '',
    lockedHeight: '',
  });

  const { selectedNode, updateNodeData, nodes } = useCustomDashboardContext();
  const [isManualEdit, setIsManualEdit] = useState(false);

  const onChangeHandler = (name: string, value: string | boolean | null) => {
    setFormValues((prevState) => {
      const updated = { ...prevState, [name]: value };

      if (selectedNode?.id) {
        updateNodeData(selectedNode.id, updated);
      }

      return updated;
    });
  };

  const findNode = nodes.find((node) => node.id === selectedNode?.id);
  const measuredWidth = findNode?.measured?.width;
  const measuredHeight = findNode?.measured?.height;

  useEffect(() => {
    if (!findNode || !selectedNode?.id) return;

    const {
      width,
      height,
      fillColor,
      strokeColor,
      strokeThickness,
      isLockRatio,
      lockedWidth,
      lockedHeight,
    } = (findNode.data || {}) as any;

    setFormValues({
      width: width ?? '',
      height: height ?? '',
      fillColor: fillColor ?? greyColor,
      strokeColor: strokeColor ?? '',
      strokeThickness: strokeThickness ?? '1px',
      isLockRatio: isLockRatio ?? false,
      lockedWidth: lockedWidth ?? '',
      lockedHeight: lockedHeight ?? '',
    });
  }, [selectedNode?.id, findNode, greyColor]);

  useEffect(() => {
    if (measuredWidth) {
      setFormValues((prev) => ({ ...prev, width: String(measuredWidth) }));
    }
  }, [measuredWidth]);

  useEffect(() => {
    if (measuredHeight) {
      setFormValues((prev) => ({ ...prev, height: String(measuredHeight) }));
    }
  }, [measuredHeight]);

  useEffect(() => {
    if (measuredWidth) {
      onChangeHandler('width', String(measuredWidth));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measuredWidth]);

  useEffect(() => {
    if (measuredHeight) {
      onChangeHandler('height', String(measuredHeight));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [measuredHeight]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <ColorPickerInput
        name="fillColor"
        onChangeHandler={onChangeHandler}
        label="Fill Color"
        color={formValues.fillColor}
      />
      <ColorPickerInput
        name="strokeColor"
        onChangeHandler={onChangeHandler}
        label="Stroke Color"
        color={formValues.strokeColor}
      />
      <Autocomplete
        key={selectedNode?.id || 'no-node'}
        size="small"
        value={formValues.strokeThickness || ''}
        inputValue={formValues.strokeThickness || ''}
        onInputChange={(_, newInputValue) => {
          onChangeHandler('strokeThickness', newInputValue || '');
        }}
        onChange={(_, selectedOption) => {
          onChangeHandler('strokeThickness', selectedOption || '');
        }}
        renderInput={(params) => <TextField {...params} label="Stroke Thickness" />}
        options={strokeTicknessOptions}
      />
      <Box sx={{ display: 'flex', gap: '16px' }}>
        <TextField
          size="small"
          value={
            isManualEdit
              ? formValues.width
              : measuredWidth
              ? String(measuredWidth)
              : formValues.width
          }
          label="Width"
          name="width"
          onChange={(e) => {
            setIsManualEdit(true);
            onChangeHandler('width', e.target.value);
          }}
          onBlur={() => setIsManualEdit(false)}
          InputProps={{
            endAdornment: <InputAdornment position="end">px</InputAdornment>,
          }}
          disabled
        />

        <TextField
          size="small"
          value={
            isManualEdit
              ? formValues.height
              : measuredHeight
              ? String(measuredHeight)
              : formValues.height
          }
          label="Height"
          name="height"
          onChange={(e) => {
            setIsManualEdit(true);
            onChangeHandler('height', e.target.value);
          }}
          onBlur={() => setIsManualEdit(false)}
          InputProps={{
            endAdornment: <InputAdornment position="end">px</InputAdornment>,
          }}
          disabled
        />

        <FormControlLabel
          control={
            <Switch
              checked={formValues.isLockRatio}
              name="isLockRatio"
              onChange={(e) => {
                const { checked } = e.target;
                onChangeHandler(e.target.name, e.target.checked);

                if (checked) {
                  onChangeHandler('lockedWidth', formValues.width);
                  onChangeHandler('lockedHeight', formValues.height);
                }
              }}
            />
          }
          label={
            <Typography fontSize="12px" noWrap>
              Lock ratio
            </Typography>
          }
        />
      </Box>
    </Box>
  );
};
